import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar,
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from 'recharts';
import AdminLayout from '../components/AdminLayout';
import { Icon } from '../utils/icons';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const tt = { borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' };

const hexToRgba = (hex, a) => { const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16); return `rgba(${r},${g},${b},${a})`; };

function KPI({ label, value, iconName, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: hexToRgba(color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={iconName} size={22} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '26px', fontWeight: '900', color: '#111827', lineHeight: 1 }}>{value}</div>
      </div>
    </div>
  );
}

function FunnelViz({ stages }) {
  const max = Math.max(...stages.map(s => s.count), 1);
  const colors = ['#3a6ad9', '#3a6ad9', '#acf44e', '#d97706'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {stages.map((s, i) => (
        <div key={s.label}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{s.label}</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{s.count.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ flex: 1, height: '10px', backgroundColor: '#f3f4f6', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{ width: `${(s.count / max) * 100}%`, height: '100%', backgroundColor: colors[i] || '#3a6ad9', borderRadius: '5px', transition: 'width 0.5s' }} />
            </div>
            {i > 0 && stages[i - 1].count > 0 && (
              <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '34px', textAlign: 'right' }}>
                {Math.round((s.count / stages[i - 1].count) * 100)}%
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DashMarketing() {
  const token = localStorage.getItem('doorly_admin_token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/dashboard/marketing-data`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <AdminLayout><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#9ca3af', fontSize: '14px' }}>Loading...</div></AdminLayout>;

  const r = data?.registrations || {};
  const f = data?.funnel || {};
  const monthly = data?.monthly || [];
  const sources = data?.sources || [];

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Marketing</h1>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>Registration, conversion funnel, and lead sources</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '28px' }}>
          <KPI label="Total Registrations" value={r.total || 0} iconName="users" color="#3a6ad9" />
          <KPI label="Registrations MTD" value={r.mtd || 0} iconName="chart" color="#ffdd86" />
          <KPI label="From Partners" value={r.from_partners || 0} iconName="users" color="#acf44e" />
          <KPI label="Apply Rate" value={`${f.apply_rate || 0}%`} iconName="arrow" color="#3a6ad9" />
          <KPI label="Approval Rate" value={`${f.approval_rate || 0}%`} iconName="check-circle" color="#acf44e" />
          <KPI label="Close Rate" value={`${f.close_rate || 0}%`} iconName="key" color="#ffdd86" />
        </div>

        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
          {/* Conversion Funnel */}
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>Conversion Funnel</h3>
            <FunnelViz stages={[
              { label: 'Registered', count: f.registered || 0 },
              { label: 'Applied', count: f.applied || 0 },
              { label: 'Approved', count: f.approved || 0 },
              { label: 'Closed', count: f.closed || 0 },
            ]} />
          </div>

          {/* Monthly Registrations */}
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Monthly Registrations</h3>
            {monthly.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthly} margin={{ top: 0, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={tt} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Bar dataKey="registrations" name="Total" fill="#3a6ad9" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="from_partners" name="From Partners" fill="#acf44e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>No data yet</div>}
          </div>
        </div>

        {/* Lead Sources Table */}
        {sources.length > 0 && (
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Lead Sources</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  {['Source', 'Total', 'MTD'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sources.map((s, i) => (
                  <tr key={s.source} style={{ borderBottom: i < sources.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                    <td style={{ padding: '10px 12px', fontWeight: '500', color: '#111827', textTransform: 'capitalize' }}>{s.source}</td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>{s.total}</td>
                    <td style={{ padding: '10px 12px', fontWeight: '600', color: '#111827' }}>{s.mtd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
